import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Calendar } from "../ui/calendar";
import { toast } from "sonner";
import { Bowtie, ArrowRight } from "../landing/icons";
import { trackEvent } from "../../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputStyle =
  "bg-ink-700 border border-white/10 focus:border-gold focus-visible:ring-1 focus-visible:ring-gold/60 focus-visible:ring-offset-0 text-white placeholder:text-white/35";

const CHALLENGES = [
  "Not enough leads",
  "Low conversion rate",
  "Inconsistent revenue",
  "No clear funnel / system",
  "Pricing & positioning",
  "Audience reach",
  "Other",
];

// Time slots offered per day for the "later" booking calendar — 30-min increments, full 24h.
const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 0; h < 24; h += 1) {
    for (const m of [0, 30]) {
      const period = h < 12 ? "AM" : "PM";
      const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const label = `${hour12}:${m === 0 ? "00" : "30"} ${period}`;
      slots.push({ h, m, label, key: `${h}-${m}` });
    }
  }
  return slots;
})();

function formatSession(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function SessionRow({ session, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(session)}
      data-testid={`session-row-${session.id}`}
      className={`w-full text-left px-5 py-4 rounded-xl border transition-all flex items-center justify-between gap-3 group
        ${selected
          ? "border-gold bg-gold/10"
          : "border-white/10 hover:border-gold/50 bg-white/[0.02]"}`}
    >
      <div>
        <div className="text-[10px] uppercase tracking-[0.24em] text-gold/80">{session.tag}</div>
        <div className="font-display text-base text-white mt-1">{session.label}</div>
        <div className="text-xs text-white/55 mt-0.5">{formatSession(session.starts_at)}</div>
      </div>
      <span className={`w-5 h-5 rounded-full border ${selected ? "border-gold bg-gold" : "border-white/30"} flex items-center justify-center`}>
        {selected ? <span className="w-1.5 h-1.5 rounded-full bg-black" /> : null}
      </span>
    </button>
  );
}

/**
 * mode: "closest" → auto-pick first session, hide picker
 * mode: "later"   → calendar date picker + time-slot grid (visitor picks any future slot)
 */
export default function RegistrationModal({ open, onOpenChange, mode = "closest" }) {
  const [step, setStep] = useState("pick"); // pick → form → success
  const [sessions, setSessions] = useState([]);
  const [selected, setSelected] = useState(null);

  // "later" mode state
  const [pickedDate, setPickedDate] = useState(null);   // Date object (midnight local)
  const [pickedSlot, setPickedSlot] = useState(null);   // { h, m, key, label }

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({
    first_name: "",
    email: "",
    phone: "",
    business: "",
    challenge: "",
  });

  // Earliest date the user can book = tomorrow (anything earlier should use Join Now)
  const minBookableDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  useEffect(() => {
    if (!open) return;
    setStep("pick");
    setSuccess(null);
    setPickedDate(null);
    setPickedSlot(null);

    if (mode === "later") {
      // Skip API — visitor will pick any custom date+time
      setSessions([]);
      setSelected(null);
      setLoading(false);
    } else {
      setLoading(true);
      axios.get(`${API}/webinar/sessions/closest`)
        .then((r) => {
          const s = r.data.session;
          setSessions([s]);
          setSelected(s);
        })
        .catch(() => toast.error("Couldn't load sessions. Try again."))
        .finally(() => setLoading(false));
    }
    trackEvent("registration_modal_open", { metadata: { mode } });
  }, [open, mode]);

  const handle = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  // Combine pickedDate + pickedSlot → ISO string
  const laterStartsAt = useMemo(() => {
    if (!pickedDate || !pickedSlot) return null;
    const d = new Date(pickedDate);
    d.setHours(pickedSlot.h, pickedSlot.m, 0, 0);
    return d.toISOString();
  }, [pickedDate, pickedSlot]);

  const proceedToForm = () => {
    if (mode === "later") {
      if (!pickedDate) return toast.error("Pick a date first");
      if (!pickedSlot) return toast.error("Pick a time slot");
      const customSession = {
        id: `custom-${laterStartsAt}`,
        starts_at: laterStartsAt,
        label: "Your scheduled session",
        tag: "your pick",
      };
      setSelected(customSession);
      setStep("form");
      trackEvent("registration_session_selected", {
        metadata: { mode: "later", starts_at: laterStartsAt },
      });
      return;
    }
    if (!selected) return toast.error("Pick a session time first");
    setStep("form");
    trackEvent("registration_session_selected", {
      metadata: { starts_at: selected.starts_at },
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) return toast.error("Please enter your first name");
    if (!validEmail(form.email)) return toast.error("Please enter a valid email");
    if (!selected) return toast.error("Pick a session time");
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/webinar/register`, {
        ...form,
        scheduled_at: selected.starts_at,
      });
      setSuccess(r.data);
      setStep("success");
      trackEvent("registration_completed", {
        registration_id: r.data.id,
        metadata: { starts_at: selected.starts_at, mode },
      });
      toast.success("You're registered. Check your email for the calendar invite.");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Registration failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="registration-modal"
        className="bg-ink-800 border border-gold/25 text-white max-w-xl rounded-2xl max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <span className="w-10 h-10 rounded-full border border-gold/40 flex items-center justify-center bg-ink-700">
              <Bowtie className="w-5 h-5 text-gold" />
            </span>
            <span className="text-[11px] uppercase tracking-[0.28em] text-gold/80">
              Free Automated Masterclass
            </span>
            <span
              data-testid="masterclass-modal-free-badge"
              className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.28em] font-bold"
              style={{
                background: "linear-gradient(135deg,#f3d172 0%,#D4AF37 60%,#a07e1f 100%)",
                color: "#0a0a0a",
              }}
            >
              Free
            </span>
          </div>
          <DialogTitle className="font-display text-3xl leading-tight">
            {step === "success"
              ? <>You're <span className="bk-gold-text italic">in.</span></>
              : <>Reserve Your <span className="bk-gold-text italic">Spot</span></>}
          </DialogTitle>
          <DialogDescription className="text-white/60">
            {step === "pick" && (mode === "closest"
              ? "Join the next available scheduled session — we've reserved the closest start time for you."
              : "Pick a date and time that works for you. We'll send a calendar invite, two reminders, and a replay link.")}
            {step === "form" && "A few quick details so we can confirm and send your calendar invite."}
            {step === "success" && "Your seat is held. Confirmation + calendar invite are on their way."}
          </DialogDescription>
        </DialogHeader>

        {/* STEP: PICK */}
        {step === "pick" && (
          <div className="mt-2 space-y-4" data-testid="registration-step-pick">
            {loading ? (
              <div className="py-12 text-center text-white/55">Loading sessions…</div>
            ) : mode === "later" ? (
              <>
                {/* Date picker */}
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 flex justify-center">
                  <Calendar
                    mode="single"
                    selected={pickedDate}
                    onSelect={(d) => { setPickedDate(d); setPickedSlot(null); }}
                    disabled={(d) => d < minBookableDate}
                    fromDate={minBookableDate}
                    initialFocus
                    data-testid="later-date-picker"
                    className="text-white"
                  />
                </div>

                {/* Time slots */}
                {pickedDate ? (
                  <div data-testid="later-time-slots">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-gold/80 mb-2">
                      Pick a time on {pickedDate.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
                    </div>
                    <div className="grid grid-cols-3 gap-2 max-h-[40vh] overflow-y-auto pr-1">
                      {TIME_SLOTS.map((slot) => (
                        <button
                          key={slot.key}
                          type="button"
                          onClick={() => setPickedSlot(slot)}
                          data-testid={`time-slot-${slot.key}`}
                          className={`px-2.5 py-2.5 rounded-lg border text-sm transition-all
                            ${pickedSlot?.key === slot.key
                              ? "border-gold bg-gold/15 text-white"
                              : "border-white/10 bg-white/[0.02] text-white/75 hover:border-gold/50 hover:text-white"}`}
                        >
                          {slot.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.01] px-4 py-5 text-center text-sm text-white/45">
                    Pick a date above to see available times.
                  </div>
                )}

                {/* Selected summary */}
                {laterStartsAt ? (
                  <div className="rounded-xl border border-gold/30 bg-gold/5 px-4 py-3">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-gold/80">Your selection</div>
                    <div className="font-display text-base text-white mt-1">{formatSession(laterStartsAt)}</div>
                  </div>
                ) : null}

                <button
                  onClick={proceedToForm}
                  data-testid="registration-continue"
                  disabled={!laterStartsAt}
                  className="w-full mt-1 py-4 rounded-full bk-shimmer text-black text-xs uppercase tracking-[0.22em] font-bold flex items-center justify-center gap-2 hover:scale-[1.01] transition-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  {sessions.map((s) => (
                    <SessionRow
                      key={s.id}
                      session={s}
                      selected={selected?.id === s.id}
                      onSelect={setSelected}
                    />
                  ))}
                </div>
                <button
                  onClick={proceedToForm}
                  data-testid="registration-continue"
                  className="w-full mt-3 py-4 rounded-full bk-shimmer text-black text-xs uppercase tracking-[0.22em] font-bold flex items-center justify-center gap-2 hover:scale-[1.01] transition-transform"
                >
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        )}

        {/* STEP: FORM */}
        {step === "form" && (
          <form onSubmit={submit} noValidate data-testid="registration-form" className="space-y-4 mt-2">
            <div className="rounded-xl border border-gold/30 bg-gold/5 px-4 py-3">
              <div className="text-[10px] uppercase tracking-[0.24em] text-gold/80">Your scheduled session</div>
              <div className="font-display text-base text-white mt-1">{formatSession(selected?.starts_at)}</div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-white/65">First name *</Label>
                <Input
                  data-testid="registration-first-name"
                  className={`mt-2 ${inputStyle}`}
                  value={form.first_name} onChange={handle("first_name")}
                  placeholder="Your first name" required
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-white/65">Email *</Label>
                <Input
                  type="email" data-testid="registration-email"
                  className={`mt-2 ${inputStyle}`}
                  value={form.email} onChange={handle("email")}
                  placeholder="you@business.com" required
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-white/65">Phone</Label>
                <Input
                  data-testid="registration-phone"
                  className={`mt-2 ${inputStyle}`}
                  value={form.phone} onChange={handle("phone")}
                  placeholder="(optional)"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-white/65">Business / website</Label>
                <Input
                  data-testid="registration-business"
                  className={`mt-2 ${inputStyle}`}
                  value={form.business} onChange={handle("business")}
                  placeholder="acme.com"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-white/65">Main business challenge</Label>
              <Select value={form.challenge} onValueChange={handle("challenge")}>
                <SelectTrigger data-testid="registration-challenge" className={`mt-2 ${inputStyle}`}>
                  <SelectValue placeholder="Select what's in your way" />
                </SelectTrigger>
                <SelectContent className="bg-ink-700 border border-white/10 text-white">
                  {CHALLENGES.map((c) => (
                    <SelectItem key={c} value={c} className="focus:bg-gold/15 focus:text-gold">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setStep("pick")}
                className="text-xs uppercase tracking-[0.22em] text-white/60 hover:text-gold transition-colors"
              >
                ← Back
              </button>
              <button
                type="submit" disabled={submitting}
                data-testid="registration-submit"
                className="flex-1 py-4 rounded-full bk-shimmer text-black text-xs uppercase tracking-[0.22em] font-bold hover:scale-[1.01] transition-transform disabled:opacity-60"
              >
                {submitting ? "Reserving..." : "Register"}
              </button>
            </div>
          </form>
        )}

        {/* STEP: SUCCESS */}
        {step === "success" && success && (
          <div className="mt-2 space-y-5" data-testid="registration-step-success">
            <div className="rounded-xl border border-gold/30 bg-gold/5 px-5 py-4">
              <div className="text-[10px] uppercase tracking-[0.24em] text-gold/80">Reserved for</div>
              <div className="font-display text-xl text-white mt-1">{formatSession(success.scheduled_at)}</div>
              <div className="text-xs text-white/55 mt-1">
                Confirmation + calendar invite sent to <span className="text-white">{success.email}</span>
              </div>
            </div>
            <div className="text-sm text-white/70 leading-relaxed">
              Check your inbox for a meeting invite (.ics) you can add to Google, Apple, or Outlook calendar. You'll also get reminders one hour and 15 minutes before your session, and a replay link if you miss it.
            </div>
            <a
              href={`/webinar/waiting-room/${success.join_token}`}
              data-testid="registration-go-waiting-room"
              className="block w-full py-4 rounded-full bk-shimmer text-black text-xs uppercase tracking-[0.22em] font-bold text-center hover:scale-[1.01] transition-transform"
            >
              Go to Waiting Room →
            </a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
